import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, TrendingUp, TrendingDown, Users, CalendarDays, DollarSign, Sparkles, ArrowRight, Info, ChevronDown, ChevronUp, Lightbulb, X, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

interface Insight {
  type: "warning" | "alert" | "success" | "info";
  category: string;
  title: string;
  description: string;
  action?: { label: string; href: string };
}

const typeConfig: Record<string, { icon: any; bg: string; border: string; iconColor: string }> = {
  warning: { icon: TrendingDown, bg: "bg-amber-50 dark:bg-amber-950/20", border: "border-amber-200/60 dark:border-amber-800/30", iconColor: "text-amber-600" },
  alert: { icon: AlertTriangle, bg: "bg-red-50 dark:bg-red-950/20", border: "border-red-200/60 dark:border-red-800/30", iconColor: "text-red-600" },
  success: { icon: TrendingUp, bg: "bg-green-50 dark:bg-green-950/20", border: "border-green-200/60 dark:border-green-800/30", iconColor: "text-green-600" },
  info: { icon: Info, bg: "bg-blue-50 dark:bg-blue-950/20", border: "border-blue-200/60 dark:border-blue-800/30", iconColor: "text-blue-600" },
};

const categoryIcon: Record<string, any> = {
  revenue: DollarSign,
  payments: DollarSign,
  operations: Sparkles,
  occupancy: CalendarDays,
  staff: Users,
};

const STORAGE_KEY_PREFIX = "staio_dismissed_insights_";
const DISMISS_DURATION_MS = 4 * 60 * 60 * 1000;

function loadDismissed(mode: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_PREFIX + mode);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const now = Date.now();
    const cleaned: Record<string, number> = {};
    for (const [key, ts] of Object.entries(parsed)) {
      if (typeof ts === "number" && now - ts < DISMISS_DURATION_MS) {
        cleaned[key] = ts;
      }
    }
    return cleaned;
  } catch {
    return {};
  }
}

function saveDismissed(mode: string, dismissed: Record<string, number>) {
  try {
    localStorage.setItem(STORAGE_KEY_PREFIX + mode, JSON.stringify(dismissed));
  } catch {}
}

interface LumiInsightsProps {
  businessMode?: "property" | "services";
}

export default function LumiInsights({ businessMode = "property" }: LumiInsightsProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [dismissed, setDismissed] = useState<Record<string, number>>(() => loadDismissed(businessMode));
  const queryClient = useQueryClient();

  useEffect(() => {
    setDismissed(loadDismissed(businessMode));
  }, [businessMode]);

  const { data, isLoading } = useQuery<{ insights: Insight[] }>({
    queryKey: ["/api/admin/lumi/insights", businessMode],
    queryFn: async () => {
      const res = await fetch(`/api/admin/lumi/insights?mode=${businessMode}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load insights");
      return res.json();
    },
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  if (isLoading || !data?.insights?.length) return null;

  const visibleInsights = data.insights.filter(i => !dismissed[i.title]);
  if (visibleInsights.length === 0) return null;

  const alertCount = visibleInsights.filter(i => i.type === "alert" || i.type === "warning").length;

  const dismissInsight = (title: string) => {
    const updated = { ...dismissed, [title]: Date.now() };
    setDismissed(updated);
    saveDismissed(businessMode, updated);
  };

  return (
    <div data-testid="lumi-insights">
      <div className="flex items-center mb-2">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-2 group flex-1 text-left"
          data-testid="btn-toggle-insights"
        >
          <Lightbulb className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-muted-foreground">
            Insights
            {alertCount > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-bold">
                {alertCount}
              </span>
            )}
          </span>
          {collapsed ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />}
        </button>
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 h-6 w-6"
          onClick={() => queryClient.invalidateQueries({ queryKey: ["/api/admin/lumi/insights", businessMode] })}
          data-testid="btn-refresh-insights"
        >
          <RefreshCw className="w-3 h-3" />
        </Button>
      </div>

      {!collapsed && (
        <div className="grid gap-2 sm:gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {visibleInsights.slice(0, 6).map((insight, i) => {
            const config = typeConfig[insight.type] || typeConfig.info;
            const CatIcon = categoryIcon[insight.category] || Info;

            return (
              <Card key={insight.title} className={`border shadow-sm ${config.bg} ${config.border} relative group`} data-testid={`insight-card-${i}`}>
                <CardContent className="p-3 sm:p-4">
                  <button
                    onClick={() => dismissInsight(insight.title)}
                    className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/5 dark:bg-white/5 hover:bg-black/15 dark:hover:bg-white/15 transition-colors"
                    data-testid={`btn-dismiss-insight-${i}`}
                    aria-label="Dismiss insight"
                  >
                    <X className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className={`p-1.5 rounded-lg ${config.bg} shrink-0`}>
                      <CatIcon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${config.iconColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-xs sm:text-sm font-semibold leading-tight" data-testid={`insight-title-${i}`}>{insight.title}</h4>
                      <p className="text-[11px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-relaxed" data-testid={`insight-desc-${i}`}>{insight.description}</p>
                      {insight.action && (
                        <Link href={insight.action.href}>
                          <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-medium text-amber-700 dark:text-amber-400 mt-1.5 sm:mt-2 hover:underline cursor-pointer" data-testid={`insight-action-${i}`}>
                            {insight.action.label}
                            <ArrowRight className="w-3 h-3" />
                          </span>
                        </Link>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
